import { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { CustomCursor } from "@/components/CustomCursor";
import { Chatbot } from "@/components/Chatbot";
import { useLenis } from "@/lib/useLenis";
import ComingSoon from "@/pages/ComingSoon";
import Home from "@/pages/Home";
import Work from "@/pages/Work";
import ServicesPage from "@/pages/ServicesPage";
import Stay from "@/pages/Stay";
import About from "@/pages/About";
import ContactPage from "@/pages/ContactPage";
import Project from "@/pages/Project";
import VentureDetail from "@/pages/VentureDetail";
import Press from "@/pages/Press";

/**
 * The one page era used hash anchors for everything. Those links live on in
 * shared messages, browser history and the odd cached page, so each old anchor
 * maps to the route that content moved to.
 */
const LEGACY_HASH_ROUTES = {
  "#work": "/work",
  "#services": "/services",
  "#ventures": "/stay",
  "#manifesto": "/about",
  "#talk": "/about#talk",
  "#contact": "/contact",
  "#mae-terra": "/ventures/mae-terra",
};

/**
 * One scroll authority for the whole router.
 *
 * Route change with no hash: to the top. Route change with a hash: retry until
 * the target mounts, then native scrollIntoView, deliberately not
 * requestAnimationFrame and not Lenis, so it still lands in a background tab.
 */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname === "/" && hash && LEGACY_HASH_ROUTES[hash]) {
      navigate(LEGACY_HASH_ROUTES[hash], { replace: true });
      return;
    }
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = hash.slice(1);
    let tries = 0;
    let timer;
    const jump = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
      } else if (tries++ < 40) {
        timer = setTimeout(jump, 50);
      }
    };
    jump();
    return () => clearTimeout(timer);
  }, [pathname, hash, navigate]);

  return null;
}

function AppShell({ children }) {
  useLenis();
  return (
    <>
      <div className="noise-overlay" aria-hidden="true" />
      <CustomCursor />
      <Nav />
      {children}
      <Footer />
      <Chatbot />
    </>
  );
}

/**
 * Launch gate.
 *
 * The client asked for a holding page while the full site is finished, so
 * every ordinary visitor sees ComingSoon on every path. The full site stays
 * reachable for review: opening any URL with ?preview=1 once sets a local
 * flag and unlocks it in that browser from then on; ?leave-preview clears it.
 * The flag is read once at mount, deliberately, so the gate cannot flap
 * mid-session.
 */
const previewUnlocked = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.has("preview")) {
      localStorage.setItem("sr-preview", "1");
      return true;
    }
    if (q.has("leave-preview")) {
      localStorage.removeItem("sr-preview");
      return false;
    }
    return localStorage.getItem("sr-preview") === "1";
  } catch (e) {
    return false;
  }
};

function App() {
  if (!previewUnlocked()) {
    return (
      <div className="App" data-testid="app-root">
        <ComingSoon />
      </div>
    );
  }
  return (
    <div className="App bg-cream text-ink" data-testid="app-root">
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <ScrollManager />
          <AppShell>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/work" element={<Work />} />
              <Route path="/work/:slug" element={<Project />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/stay" element={<Stay />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/ventures/:slug" element={<VentureDetail />} />
              <Route path="/press" element={<Press />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </AppShell>
        </BrowserRouter>
      </MotionConfig>
    </div>
  );
}

export default App;
